import { ReactElement, createContext, useContext, useState } from 'react';
import { Nullable } from '../types';
import { Alert, Snackbar, SxProps } from '@mui/material';
import Typography from '../componentsv2/Typography';
import { CloseCircleIcon, CheckCircleIcon } from '../components/Icons';

type SnackbarType = 'error' | 'success';

const SnackbarContext = createContext<{ showSnackbar: (message: string, type: SnackbarType) => void }>({
  showSnackbar: () => {},
});

export function useSnackbar() {
  return useContext(SnackbarContext);
}

const typeIconMap: Record<SnackbarType, ReactElement> = {
  error: <CloseCircleIcon color="#FFFAFA" />,
  success: <CheckCircleIcon color="#FDFDFD" />,
};

const typeColorMap: Record<SnackbarType, SxProps> = {
  error: { color: '#FFFAFA', background: '#C54646' },
  success: { color: '#F7FDF8', background: '#0EA480' },
};

const SNACKBAR_DURATION = 4000;

export function GlobalSnackbarProvider({ children }: any) {
  const [snackbarData, setSnackbarData] = useState<Nullable<{ message: string; type: SnackbarType }>>(null);

  const showSnackbar = (message: string, type: SnackbarType) => {
    setSnackbarData({ message, type });
  };

  const hideSnackbar = () => {
    setSnackbarData(null);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {!!snackbarData && (
        <Snackbar
          open={true}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={SNACKBAR_DURATION}
          ClickAwayListenerProps={{ onClickAway: () => null }} // prevent clsoing by clicking outside the snackbar
          onClose={() => {
            hideSnackbar();
          }}
        >
          <Alert
            onClose={() => {
              hideSnackbar();
            }}
            icon={typeIconMap[snackbarData.type]}
            sx={{
              width: '100%',
              ...typeColorMap[snackbarData?.type],
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: 'inherit' }}>{snackbarData.message}</Typography>
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
}
